import type { CSSProperties, JSX, ReactNode } from "react";
import { cx } from "../cx";

export type CardVariant = "outlined" | "plain";

export interface CardProps {
  /** Caps eyebrow label above the title. */
  label?: string | undefined;
  title?: ReactNode | undefined;
  /** Hairline-separated footer row. */
  footer?: ReactNode | undefined;
  /** Blue-900 surface for dark sections. */
  dark?: boolean | undefined;
  /**
   * `outlined` (default) marks an inspectable object; `plain` drops the
   * border, background, and padding for pure layout grouping so bordered
   * cards keep meaning.
   */
  variant?: CardVariant | undefined;
  className?: string | undefined;
  style?: CSSProperties | undefined;
  children?: ReactNode | undefined;
}

/**
 * Content card — white, hairline border, 4px radius, never drop-shadowed.
 * Ported from design/tarski-kit/components/display/Card.jsx.
 */
export function Card({
  label,
  title,
  footer,
  dark = false,
  variant = "outlined",
  className,
  style,
  children,
}: CardProps): JSX.Element {
  return (
    <div
      className={cx(
        "tk-card",
        dark && "tk-card--dark",
        variant === "plain" && "tk-card--plain",
        className,
      )}
      style={style}
    >
      {label === undefined ? null : <div className="tk-card__label">{label}</div>}
      {title === undefined ? null : <div className="tk-card__title">{title}</div>}
      <div className="tk-card__body">{children}</div>
      {footer === undefined ? null : <div className="tk-card__footer">{footer}</div>}
    </div>
  );
}
